import React from 'react';
import { PopPanel } from '../Dialog/PopParts';
import {
    Steps, Tip, Roles, Chain, BigTiles, Cards, PhoneStates, WordRules,
    QuestionRules, BannedWords, WrongQuestions,
    DoubleStates, ScoreList, MsgList, BottomButtons
} from './GuideParts';

/*
    Contenuti della guida: 7 capitoli, ognuno diviso in schede.
    body riceve il colore del capitolo (per i numeri dei passaggi).
*/
export const chapters = [
    {
        title: "Regole del gioco",
        color: "fire",
        icon: "trophy",
        schede: [
            {
                title: "Chi gioca?",
                lead: "Ogni squadra è formata da tre giocatori.",
                body: () => (
                    <>
                        <Roles />
                        <Chain />
                        <Tip>L'indovino tiene in mano il telefono con il pulsante: appena ha capito preme il <b>pulsante rosso</b>, il tempo si ferma e può dare la risposta.</Tip>
                    </>
                ),
            },
            {
                title: "Il turno",
                lead: "Ogni squadra ha 60 secondi per indovinare quante più parole possibili.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "L'indovino preme il pulsante rosso: sul display compare la parola per i suggeritori.",
                            "I suggeritori costruiscono la frase, una parola a testa.",
                            "Appena ha capito, l'indovino preme di nuovo il pulsante: il tempo si ferma.",
                            "L'indovino dà la risposta e la segna sul telefono: CORRETTA o ERRATA. Poi si passa alla parola successiva.",
                        ]} />
                        <Tip>Allo scadere del tempo l'indovino non è obbligato a rispondere all'ultima domanda e può toccare SALTA, a meno che non abbia già premuto il pulsante: allora deve rispondere. Se i suggeritori continuano a parlare dopo la fine, la domanda è annullata.</Tip>
                    </>
                ),
            },
            {
                title: "Come si formula la domanda",
                lead: "I suggeritori inventano una domanda in italiano, una parola a testa.",
                body: () => (
                    <>
                        <Chain hidden="ZEBRA" words={["QUAL È", "L'ANIMALE", "A STRISCE", "DELL'AFRICA?"]}>
                            Articoli, preposizioni, pronomi e parole con l'apostrofo si dicono insieme alla parola che segue: <b>contano come una sola</b>.
                        </Chain>
                        <QuestionRules />
                    </>
                ),
            },
            {
                title: "Parole vietate",
                lead: "Nella domanda non possono comparire queste parole.",
                body: () => <BannedWords />,
            },
            {
                title: "Formulazioni errate",
                lead: "Una domanda errata viene annullata e si passa alla parola successiva: succede con parole vietate, errori di grammatica e nei casi qui sotto.",
                body: () => (
                    <>
                        <ScoreList rows={[
                            { btn: "ERRATA", color: "red", what: "Domanda annullata", pts: "−1" },
                        ]} />
                        <WrongQuestions />
                    </>
                ),
            },
            {
                title: "Passo e raddoppio",
                lead: "Ogni squadra può usarli un numero limitato di volte.",
                body: () => (
                    <>
                        <BigTiles items={[
                            { value: "3", color: "orange", label: "Passo a squadra" },
                            { value: "2", color: "navy", label: "Raddoppi a squadra" },
                        ]} />
                        <PopPanel className="guide-rule">
                            <span className="guide-rule-title">Passo</span>
                            <span className="pop-txt">Salta la parola troppo difficile, senza perdere punti. Per farlo, un suggeritore, durante il suo turno, dice "Passo!", l'indovino deve fermare il tempo; terminati i 3 passo bisogna provare a formulare lo stesso la domanda.</span>
                        </PopPanel>
                        <PopPanel className="guide-rule">
                            <span className="guide-rule-title">Raddoppio</span>
                            <span className="pop-txt">La parola successiva è doppia: due parole da indovinare insieme, che valgono il doppio. Si può attivare solo con almeno 2 punti.</span>
                            <span className="pop-txt">Il gruppo di parole può essere un modo di dire, un titolo di film, canzone o romanzo, oppure un nome e cognome.</span>
                            <div className="pop-chips">
                                <span className="pop-chip">IN BOCCA AL LUPO</span>
                                <span className="pop-chip">IL RE LEONE</span>
                                <span className="pop-chip">LEONARDO DA VINCI</span>
                            </div>
                        </PopPanel>
                    </>
                ),
            },
            {
                title: "Punteggio e classifica",
                lead: "Ogni risposta cambia il punteggio della squadra.",
                body: () => (
                    <>
                        <ScoreList rows={[
                            { btn: "CORRETTA", color: "green", what: "Parola indovinata", pts: "+1" },
                            { btn: "CORRETTA", color: "green", what: "Parola doppia indovinata", pts: "+2" },
                            { btn: "ERRATA", color: "red", what: "Risposta sbagliata", pts: "−1" },
                            { btn: "ERRATA", color: "red", what: "Parola doppia sbagliata", pts: "−2" },
                            { btn: "PASSO", color: "orange", what: "Parola saltata", pts: "0" },
                        ]} />
                        <PopPanel className="guide-rule">
                            <span className="pop-txt">Il punteggio non scende mai sotto zero.</span>
                            <span className="pop-txt">Quando tutte le squadre hanno giocato, il display mostra la <b>classifica finale</b>.</span>
                        </PopPanel>
                    </>
                ),
            },
        ],
    },
    {
        title: "Avviare una partita",
        color: "blue",
        icon: "tv",
        schede: [
            {
                title: "Display e pulsante",
                lead: "Per giocare servono due dispositivi.",
                body: () => (
                    <>
                        <Cards items={[
                            { icon: "tv", title: "Display", txt: "Uno schermo che vedono tutti: TV, computer o tablet. Mostra la parola ai suggeritori, il tempo e i punti." },
                            { icon: "buzzer", title: "Pulsante", txt: "Un telefono. Serve a creare le squadre e, in partita, lo tiene in mano l'indovino: è il pulsante rosso." },
                        ]} />
                        <Tip>L'indovino non deve guardare il display: la parola è scritta lì!</Tip>
                    </>
                ),
            },
            {
                title: "Sul display",
                lead: "Avvia la partita dal dispositivo che farà da display.",
                body: (color) => (
                    <Steps color={color} items={[
                        "Tocca «Gioca ora!».",
                        "Scegli DISPLAY.",
                        "Compaiono il QR e il codice della partita: lasciali a schermo finché il telefono non si collega.",
                    ]} />
                ),
            },
            {
                title: "Sul telefono",
                lead: "Collega il telefono che farà da pulsante.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "Inquadra il QR con la fotocamera: entri direttamente nella partita.",
                            "In alternativa apri il gioco, tocca «Gioca ora!», scegli PULSANTE e scrivi il codice di 6 cifre.",
                            "Appena il telefono è collegato, puoi creare le squadre.",
                        ]} />
                        <Tip>Durante la partita il telefono con il pulsante va in mano all'indovino.</Tip>
                    </>
                ),
            },
        ],
    },
    {
        title: "Squadre",
        color: "green",
        icon: "team",
        schede: [
            {
                title: "Creare le squadre",
                lead: "Le squadre si creano dal telefono collegato come pulsante.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "Tocca «Aggiungi squadra».",
                            "Scrivi il nome, al massimo 13 caratteri, e tocca «Salva».",
                            "Quando ci sono tutte le squadre, tocca «Inizia!».",
                        ]} />
                        <Tip>Il nome dovrebbe includere un articolo, ad esempio «I FULMINI».</Tip>
                    </>
                ),
            },
            {
                title: "Le mie squadre",
                lead: "Le squadre delle partite che inizi vengono salvate sul telefono.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "Tocca «Aggiungi squadra», poi «Le mie squadre».",
                            "Spunta le squadre da aggiungere alla partita.",
                            "Tocca «Conferma».",
                        ]} />
                        <p className="pop-txt">Con il cestino togli una squadra dall'elenco di quelle salvate.</p>
                    </>
                ),
            },
            {
                title: "Modificare o eliminare",
                lead: "Tocca una squadra nell'elenco per modificarla.",
                body: (color) => (
                    <Steps color={color} items={[
                        "Cambia il nome e tocca «Salva».",
                        "Oppure tocca il pulsante rosso con il cestino per toglierla dalla partita.",
                    ]} />
                ),
            },
        ],
    },
    {
        title: "Parole personalizzate",
        color: "pink",
        icon: "cards",
        schede: [
            {
                title: "Gruppi di parole",
                lead: "Le tue parole si organizzano in gruppi.",
                body: (color) => (
                    <Steps color={color} items={[
                        "Nella schermata delle squadre tocca «Parole personaliz.».",
                        "Tocca «Crea gruppo» e dagli un nome, al massimo 13 caratteri.",
                        "Spunta un gruppo per usare tutte le sue parole, oppure aprilo per sceglierle una per una.",
                    ]} />
                ),
            },
            {
                title: "Aggiungere parole",
                lead: "Apri un gruppo e tocca «Aggiungi».",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "Scrivi una parola e tocca «Aggiungi».",
                            "Il popup resta aperto: continua con la parola successiva.",
                            "Tocca «Fine» quando hai finito.",
                        ]} />
                        <Tip>Ogni parola nuova è già spuntata: verrà usata nella prossima partita.</Tip>
                    </>
                ),
            },
            {
                title: "Formato ideale delle parole",
                lead: "Tre regole per parole che funzionano in partita.",
                body: () => <WordRules />,
            },
            {
                title: "Standard e personalizzate",
                lead: "Scegli quali parole usare in partita.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "In «Parole in partita» spunta Standard, Personalizzate o entrambe.",
                            "Con entrambe, la barra decide il mix: al 70% circa 7 parole su 10 sono standard.",
                        ]} />
                        <Tip>Con meno di 5 parole personalizzate, o meno di 2 per il raddoppio, il gioco usa anche quelle standard.</Tip>
                    </>
                ),
            },
        ],
    },
    {
        title: "Il pulsante",
        color: "red",
        icon: "buzzer",
        schede: [
            {
                title: "Stati del pulsante",
                lead: "Il telefono con il pulsante lo tiene l'indovino: cambia schermata a ogni fase del turno.",
                body: () => <PhoneStates />,
            },
            {
                title: "Passo e raddoppio in pratica",
                lead: "Due pulsanti speciali, da usare al momento giusto.",
                body: () => (
                    <>
                        <DoubleStates>
                            Compare sotto il pulsante rosso, prima di generare la parola. Si attiva con almeno 2 punti.
                        </DoubleStates>
                        <PopPanel className="guide-rule">
                            <span className="guide-rule-title">Pulsante PASSO</span>
                            <span className="pop-txt">Compare dopo aver fermato il tempo, sotto CORRETTA ed ERRATA. Quando il tempo è finito diventa SALTA.</span>
                        </PopPanel>
                    </>
                ),
            },
            {
                title: "Segnare la risposta",
                lead: "Dopo aver fermato il tempo, l'indovino dice la risposta e la segna sul telefono.",
                body: () => (
                    <>
                        <ScoreList rows={[
                            { btn: "CORRETTA", color: "green", what: "Ha indovinato", pts: "+1" },
                            { btn: "ERRATA", color: "red", what: "Ha sbagliato", pts: "−1" },
                            { btn: "PASSO", color: "orange", what: "Salta la parola", pts: "0" },
                        ]} />
                        <Tip>Con il raddoppio attivo la risposta vale il doppio: +2 oppure −2.</Tip>
                    </>
                ),
            },
        ],
    },
    {
        title: "Impostazioni di gioco",
        color: "navy",
        icon: "gear",
        schede: [
            {
                title: "Musica ed effetti",
                lead: "Le impostazioni si aprono con il pulsante arancione in basso a sinistra.",
                body: () => (
                    <>
                        <BottomButtons highlight="settings" />
                        <p className="pop-txt">Puoi regolare separatamente il volume della musica e quello degli effetti.</p>
                        <Tip>Se sei collegato come pulsante, regoli i volumi del display.</Tip>
                    </>
                ),
            },
            {
                title: "Schermo intero",
                lead: "Il pulsante blu in basso a destra mette il gioco a tutto schermo.",
                body: () => (
                    <>
                        <BottomButtons highlight="fullscreen" />
                        <p className="pop-txt">Toccalo di nuovo per uscire dallo schermo intero.</p>
                    </>
                ),
            },
            {
                title: "Eliminare i dati",
                lead: "Riporta il gioco come la prima volta che l'hai aperto.",
                body: (color) => (
                    <>
                        <Steps color={color} items={[
                            "Apri le impostazioni e tocca «Elimina dati».",
                            "Conferma con «Elimina»: l'applicazione si riavvia.",
                        ]} />
                        <PopPanel className="guide-rule">
                            <span className="pop-cap">Verranno cancellati</span>
                            <div className="pop-chips">
                                <span className="pop-chip">Le mie squadre</span>
                                <span className="pop-chip">Parole personalizzate</span>
                            </div>
                        </PopPanel>
                    </>
                ),
            },
        ],
    },
    {
        title: "Se qualcosa va storto",
        color: "cyan",
        icon: "buoy",
        schede: [
            {
                title: "Non riesco a entrare",
                lead: "Se il telefono non si collega, il gioco ti dice perché.",
                body: () => (
                    <MsgList items={[
                        { msg: "La partita non esiste oppure è terminata.", fix: "Controlla il codice sul display: sono 6 cifre. Se la partita è finita, creane una nuova." },
                        { msg: "La partita è già connessa ad un pulsante.", fix: "Un altro telefono è già collegato: per ogni partita ne serve uno solo." },
                    ]} />
                ),
            },
            {
                title: "Qualcuno si è disconnesso",
                lead: "Una connessione instabile può interrompere la partita.",
                body: () => (
                    <MsgList items={[
                        { msg: "Il pulsante si è disconnesso.", fix: "Sul display ricompaiono il QR e il codice: collega di nuovo un telefono e la partita riprende." },
                        { msg: "L'host si è disconnesso.", fix: "Il display ha lasciato la partita: creane una nuova per continuare a giocare." },
                        { msg: "Ti sei disconnesso.", fix: "Controlla la connessione di rete e riprova." },
                    ]} />
                ),
            },
        ],
    },
];
